import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { purchaseReturnService } from "../utils/apiService";
import CircularProgress from "@mui/material/CircularProgress";
import PremiumInvoiceViewDialog from "../components/InvoiceViewDialog";
import SelectPurchaseInvoiceDialog from "../components/purchase-return/SelectPurchaseInvoiceDialog";
import ExportButton from "../components/ExportButton";
import image1 from "../assets/riyal.png";
import { useTranslation } from "../hooks/useTranslation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

function PurchaseReturn() {
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({});
  const { t } = useTranslation();
  //   const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);

  const navigate = useNavigate();
  const { invoiceNo } = useParams();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectInvoiceDialogOpen, setSelectInvoiceDialogOpen] = useState(false);
  const [selectedViewReturn, setSelectedViewReturn] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchPurchaseReturns = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const data = await purchaseReturnService.getPurchaseReturns({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
        sortBy: "entryNo",
        sortOrder: "asc",
        ...filters,
      });
      console.log("Raw purchase returns data:", data);

      const returnsArray =
        data?.data || data?.returns || data?.purchaseReturns || [];
      const arrayData = Array.isArray(returnsArray) ? returnsArray : [];

      // Sort arrayData by entryNo in ascending order
      const sortedArrayData = [...arrayData].sort((a, b) => {
        const valA =
          a.entryNo !== undefined && a.entryNo !== null && a.entryNo !== ""
            ? a.entryNo
            : null;
        const valB =
          b.entryNo !== undefined && b.entryNo !== null && b.entryNo !== ""
            ? b.entryNo
            : null;

        if (valA !== null && valB !== null) {
          return String(valA).localeCompare(String(valB), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        }
        if (valA !== null) return -1;
        if (valB !== null) return 1;
        return 0;
      });

      setPurchaseReturns(sortedArrayData);
      if (data?.pagination) {
        setTotalRows(data.pagination.total || arrayData.length);
      } else {
        setTotalRows(arrayData.length);
      }
    } catch (error) {
      console.error("Error fetching purchase returns:", error);
    } finally {
      setLoading(false);
    }
  }, [navigate, page, rowsPerPage, searchQuery, filters]);

  useEffect(() => {
    fetchPurchaseReturns();
  }, [fetchPurchaseReturns]);

  useEffect(() => {
    if (invoiceNo) {
      const fetchReturnDetails = async () => {
        if (!invoiceNo || invoiceNo === "undefined" || invoiceNo === "null") {
          console.error("Invalid invoice number provided");
          setViewDialogOpen(false);
          navigate("/purchase-return");
          return;
        }

        try {
          setViewDialogOpen(true);
          setViewLoading(true);
          const response =
            await purchaseReturnService.getPurchaseReturnByInvoiceNo(invoiceNo);

          const returnData = response?.data || response;

          if (returnData && returnData.master) {
            // Map purchase return specific fields to InvoiceViewDialog expected format
            const rawStatus = (
              returnData.master.paymentStatus ||
              returnData.paymentStatus ||
              ""
            ).toLowerCase();
            const mappedPaymentStatus =
              !rawStatus || rawStatus === "pending" || rawStatus === "paid"
                ? "Paid"
                : returnData.master.paymentStatus || returnData.paymentStatus;
            const mappedData = {
              ...returnData,
              paymentStatus: mappedPaymentStatus,
              master: {
                ...returnData.master,
                paymentStatus: mappedPaymentStatus,
                // InvoiceViewDialog expects customerName, map from supplierId
                customerName: returnData.master.supplierId?.name || "",
                customerType: returnData.master.supplierId?.code || "",
                customerPhone: returnData.master.supplierId?.phone || "",
                customerAddress:
                  [
                    returnData.master.supplierId?.address?.street,
                    returnData.master.supplierId?.address?.city,
                    returnData.master.supplierId?.address?.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "",
                // Map amounts
                subTotal: returnData.master.subtotal,
                vatAmt: returnData.master.totalTax,
                totalAmt: returnData.master.totalAmount,
                balanceAmt: returnData.master.balanceAmt,
                discountAmt:
                  returnData.master.discountAmount ??
                  returnData.master.discountAmt ??
                  0,
                discountPerc:
                  returnData.master.discountPerc ??
                  returnData.master.discountPercentage ??
                  0,
              },
            };
            setSelectedViewReturn(mappedData);
          } else if (returnData) {
            const rawStatus = (returnData.paymentStatus || "").toLowerCase();
            const mappedPaymentStatus =
              !rawStatus || rawStatus === "pending" || rawStatus === "paid"
                ? "Paid"
                : returnData.paymentStatus;
            const mappedData = {
              ...returnData,
              paymentStatus: mappedPaymentStatus,
              master: returnData.master
                ? {
                    ...returnData.master,
                    paymentStatus: mappedPaymentStatus,
                  }
                : {
                    ...returnData,
                    paymentStatus: mappedPaymentStatus,
                  },
            };
            setSelectedViewReturn(mappedData);
          } else {
            throw new Error("Invalid data structure returned from API");
          }
        } catch (error) {
          console.error("Error fetching return details:", error);
          alert(
            "Failed to load purchase return details. Invalid or missing invoice number.",
          );
          setViewDialogOpen(false);
          navigate("/purchase-return");
        } finally {
          setViewLoading(false);
        }
      };
      fetchReturnDetails();
    } else {
      setViewDialogOpen(false);
      setSelectedViewReturn(null);
    }
  }, [invoiceNo, navigate]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === "all") {
        delete newFilters[filterType];
      } else {
        newFilters[filterType] = value;
      }
      return newFilters;
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
    handleFilterClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedReturns = purchaseReturns;

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            {t("PurchaseReturns.Purchase_Returns")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t("PurchaseReturns.Manage_your_purchase_returns")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <ExportButton
            columns={[
              { field: "Entry No", headerName: t("PurchaseReturns.Entry_No") },
              {
                field: "Return #",
                headerName: t("PurchaseReturns.Return_Number"),
              },
              {
                field: "Invoice Date",
                headerName: t("PurchaseReturns.Invoice_Date"),
              },
              {
                field: "Ref Invoice No",
                headerName: t("PurchaseReturns.Reference_Invoice_Number"),
              },
              {
                field: "Supplier Name",
                headerName: t("PurchaseReturns.Supplier_Name"),
              },
              {
                field: "Supplier Code",
                headerName: t("PurchaseReturns.Supplier_Code"),
              },
              { field: "Reason", headerName: t("PurchaseReturns.Reason") },
              { field: "Subtotal", headerName: t("PurchaseReturns.Subtotal") },
              {
                field: "Tax Amount",
                headerName: t("PurchaseReturns.Tax_Amount"),
              },
              {
                field: "Total Amount",
                headerName: t("PurchaseReturns.Total_Amount"),
              },
              {
                field: "Payment Status",
                headerName: t("PurchaseReturns.Payment_Status"),
              },
              { field: "Status", headerName: t("PurchaseReturns.Status") },
              {
                field: "Created Date",
                headerName: t("PurchaseReturns.Created_Date"),
              },
            ]}
            data={purchaseReturns.map((ret, index) => ({
              "Entry No":
                ret.entryNo !== undefined &&
                ret.entryNo !== null &&
                ret.entryNo !== ""
                  ? ret.entryNo
                  : page * rowsPerPage + index + 1,
              "Return #": ret.invoiceNo || ret.returnNumber,
              "Invoice Date": ret.invoiceDate
                ? new Date(ret.invoiceDate).toLocaleDateString()
                : "",
              "Ref Invoice No": ret.refInvoiceNo,
              "Supplier Name": ret.supplierId?.name,
              "Supplier Code": ret.supplierId?.code,
              Reason: ret.reason,
              Subtotal: (ret.subtotal || 0).toFixed(2),
              "Tax Amount": (ret.totalTax || 0).toFixed(2),
              "Total Amount": (ret.totalAmount || 0).toFixed(2),
              "Payment Status":
                !ret.paymentStatus ||
                ret.paymentStatus.toLowerCase() === "pending" ||
                ret.paymentStatus.toLowerCase() === "paid"
                  ? "Paid"
                  : ret.paymentStatus,
              Status: ret.status,
              "Created Date": ret.createdAt
                ? new Date(ret.createdAt).toLocaleDateString()
                : "",
            }))}
            filename="purchase_returns"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setSelectInvoiceDialogOpen(true)}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              py: 0.5,
              px: 1,
            }}
          >
            {t("PurchaseReturns.New_Return")}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder={t("PurchaseReturns.Search_returns")}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            maxWidth: 500,
            height: 28,
            "& .MuiOutlinedInput-root": {
              borderRadius: "2px",
              height: 28,
            },
            "& .MuiInputBase-input": {
              py: 0.5,
            },
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          size="small"
          onClick={handleFilterClick}
          sx={{
            borderRadius: "2px",
            textTransform: "none",
            py: 0.25,
            px: 1,
            height: 28,
            ml: 1,
          }}
        >
          {t("Common.Filters")}
        </Button>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { width: 200 },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Common.Status")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {["all", "paid", "pending", "draft"].map((status) => (
              <Button
                key={status}
                size="small"
                variant={
                  filters.status === status ||
                  (status === "all" && !filters.status)
                    ? "contained"
                    : "outlined"
                }
                onClick={() => handleFilterChange("status", status)}
                sx={{ textTransform: "capitalize" }}
              >
                {t(`Common.Status_${status}`)}
              </Button>
            ))}
          </Box>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              {t("PurchaseReturns.Clear_Filters")}
            </Button>
          </Box>
        </Box>
      </Menu>

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          mb: 0.5,
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: "4px",
        }}
      >
        <Box>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table
              size="small"
              sx={{ minWidth: 1910, tableLayout: "auto", width: "100%" }}
            >
              <TableHead>
                <TableRow
                  sx={{ height: "44px", bgcolor: "rgba(0, 0, 0, 0.02)" }}
                >
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 150,
                      width: 150,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Return_Number")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 130,
                      width: 130,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Invoice_Date")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 130,
                      width: 130,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Reference_Invoice_Number")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 120,
                      width: 120,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Supplier")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 40,
                      width: 40,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Supplier_Code")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 40,
                      width: 40,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Subtotal")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 60,
                      width: 60,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Tax")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 60,
                      width: 60,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Total")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 130,
                      width: 130,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Payment")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 150,
                      width: 150,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Created_Date")}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      whiteSpace: "nowrap",
                      minWidth: 30,
                      width: 30,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("PurchaseReturns.Actions")}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow sx={{ height: "44px" }}>
                    <TableCell
                      colSpan={14}
                      align="center"
                      sx={{ py: 2, verticalAlign: "middle" }}
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedReturns.length === 0 ? (
                  <TableRow sx={{ height: "44px" }}>
                    <TableCell
                      colSpan={14}
                      align="center"
                      sx={{ py: 2, verticalAlign: "middle" }}
                    >
                      No purchase returns found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReturns.map((ret, index) => {
                    const entryNoValue =
                      ret.entryNo !== undefined &&
                      ret.entryNo !== null &&
                      ret.entryNo !== ""
                        ? ret.entryNo
                        : page * rowsPerPage + index + 1;
                    return (
                      <TableRow
                        key={ret._id || index}
                        hover
                        sx={{
                          height: "44px",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        {/* <TableCell align="left" sx={{ py: 0.5, px: 1, whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: 100, width: 100 }}>{entryNoValue}</TableCell> */}
                        <TableCell
                          align="left"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 150,
                            width: 150,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="primary"
                            fontWeight="medium"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {ret.invoiceNo ||
                              ret.returnNumber ||
                              ret.purchaseReturnNo ||
                              "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 130,
                            width: 130,
                          }}
                        >
                          {ret.invoiceDate
                            ? new Date(ret.invoiceDate).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 150,
                            width: 150,
                          }}
                        >
                          {ret.refInvoiceNo || "-"}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 120,
                            width: 120,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ret.supplierId?.name || "-"}
                          </Box>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 40,
                            width: 40,
                          }}
                        >
                          {ret.supplierId?.code || "-"}
                        </TableCell>
                        {/* <TableCell align="left" sx={{ py: 0.5, px: 1, whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: 150, width: 150 }}>
                                                <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {ret.reason || '-'}
                                                </Box>
                                            </TableCell> */}
                        <TableCell
                          align="right"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 40,
                            width: 40,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              width: "100%",
                            }}
                          >
                            <img
                              src={image1}
                              alt="Currency"
                              style={{
                                width: "13px",
                                height: "13px",
                                marginRight: "4px",
                                verticalAlign: "middle",
                              }}
                            />
                            <span>{(ret.subtotal || 0).toFixed(2)}</span>
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 60,
                            width: 60,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              width: "100%",
                            }}
                          >
                            <img
                              src={image1}
                              alt="Currency"
                              style={{
                                width: "13px",
                                height: "13px",
                                marginRight: "4px",
                                verticalAlign: "middle",
                              }}
                            />
                            <span>{(ret.totalTax || 0).toFixed(2)}</span>
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 60,
                            width: 60,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              width: "100%",
                            }}
                          >
                            <img
                              src={image1}
                              alt="Currency"
                              style={{
                                width: "13px",
                                height: "13px",
                                marginRight: "4px",
                                verticalAlign: "middle",
                              }}
                            />
                            <span>{(ret.totalAmount || 0).toFixed(2)}</span>
                          </Box>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 130,
                            width: 130,
                          }}
                        >
                          {(() => {
                            const rawStatus = (
                              ret.paymentStatus || "paid"
                            ).toLowerCase();
                            const isPaidOrPending =
                              !ret.paymentStatus ||
                              rawStatus === "pending" ||
                              rawStatus === "paid";
                            const displayStatus = isPaidOrPending
                              ? "RECEIVED"
                              : ret.paymentStatus.charAt(0).toUpperCase() +
                                ret.paymentStatus.slice(1);
                            const statusColor = isPaidOrPending
                              ? "success.main"
                              : rawStatus === "partial"
                                ? "warning.main"
                                : "error.main";
                            return (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: statusColor,
                                  textTransform: "capitalize",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {displayStatus}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        {/* <TableCell align="center" sx={{ py: 0.5, px: 1, whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: 120, width: 120 }}>
                                                <Typography variant="body2" sx={{
                                                    color: ret.status === 'completed' ? 'success.main' : 'text.secondary',
                                                    textTransform: 'capitalize',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {ret.status || '-'}
                                                </Typography>
                                            </TableCell> */}
                        <TableCell
                          align="left"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 150,
                            width: 150,
                          }}
                        >
                          {ret.createdAt
                            ? new Date(ret.createdAt).toLocaleDateString()
                            : "-"}
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            py: 0.5,
                            px: 1,
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            minWidth: 30,
                            width: 30,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ mx: 0.25, p: 0.5 }}
                              onClick={() => {
                                const invNo =
                                  ret.invoiceNo ||
                                  ret.returnNumber ||
                                  ret.purchaseReturnNo;
                                if (invNo) {
                                  navigate(`/purchase-returns/view/${invNo}`);
                                } else {
                                  console.error(
                                    "No invoice number available for this return",
                                  );
                                }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              "& .MuiTablePagination-select": {
                py: 0.5,
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.875rem",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Select Invoice Dialog */}
      <SelectPurchaseInvoiceDialog
        open={selectInvoiceDialogOpen}
        onClose={() => setSelectInvoiceDialogOpen(false)}
        onSelectInvoice={(invoice) => {
          // Navigate to the new purchase return page with the selected invoice data
          navigate(`/purchase-return/new?invoiceId=${invoice._id}`, {
            state: { selectedInvoice: invoice },
          });
        }}
      />

      {/* Invoice View Dialog */}
      <PremiumInvoiceViewDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedViewReturn(null);
          navigate("/purchase-return");
        }}
        invoice={selectedViewReturn}
        loading={viewLoading}
        title="Purchase Return Details"
        currencySymbol="SAR"
        onPrint={() => window.print()}
      />
    </Container>
  );
}

export default PurchaseReturn;
